import React from 'react';
import { BarChart, DollarSign, TrendingUp } from 'lucide-react';

interface EstimateResultsProps {
  estimate: {
    lowEstimate: number;
    highEstimate: number;
    confidence: number;
    marketTrend: string;
    marketTrendDescription: string;
  };
}

const EstimateResults: React.FC<EstimateResultsProps> = ({ estimate }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold mb-6 flex items-center">
        <BarChart className="h-6 w-6 text-indigo-600 mr-2" />
        Global Box Office Estimate
      </h2>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Low Estimate</div>
            <div className="text-2xl font-bold text-gray-900 flex items-center">
              <DollarSign className="h-6 w-6 text-green-600 mr-1" />
              {formatCurrency(estimate.lowEstimate)}
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">High Estimate</div>
            <div className="text-2xl font-bold text-gray-900 flex items-center">
              <DollarSign className="h-6 w-6 text-green-600 mr-1" />
              {formatCurrency(estimate.highEstimate)}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Confidence Level</div>
            <div className="flex items-center">
              <div className="flex-1 bg-gray-200 rounded-full h-4">
                <div 
                  className={`rounded-full h-4 ${
                    estimate.confidence >= 80 ? 'bg-green-600' :
                    estimate.confidence >= 70 ? 'bg-blue-600' :
                    estimate.confidence >= 60 ? 'bg-yellow-600' : 'bg-red-600'
                  }`}
                  style={{ width: `${estimate.confidence}%` }}
                />
              </div>
              <span className="ml-3 font-medium">{estimate.confidence}%</span>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Market Trend</div>
            <div className="flex items-center text-lg font-medium">
              <TrendingUp className={`h-5 w-5 mr-2 ${
                estimate.marketTrend === 'Strong Growth' ? 'text-green-600' :
                estimate.marketTrend === 'Moderate Growth' ? 'text-blue-600' :
                estimate.marketTrend === 'Stable' ? 'text-yellow-600' : 'text-gray-600'
              }`} />
              <div>
                <div>{estimate.marketTrend}</div>
                <div className="text-sm font-normal text-gray-600">
                  {estimate.marketTrendDescription}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EstimateResults;